import { AddOutlined, DeleteOutlineOutlined, VisibilityOutlined } from "@mui/icons-material";
import { Alert, Box, CircularProgress, IconButton, Snackbar, Switch } from "@mui/material";
import type { CSSProperties, FocusEvent, KeyboardEvent } from "react";
import { useEffect, useState } from "react";
import GroupChildDialog from "@/components/usersOrgs/GroupChildDialog";
import GroupView from "@/components/usersOrgs/GroupView";
import { useDeleteGroup, useGroups, useUpdateGroup } from "@/hooks/useGroups";
import { usePermissions } from "@/hooks/usePermissions";
import type { AccountDeepLink } from "@/interfaces/accountNav";
import type { GroupNode } from "@/interfaces/group";
import type { Organization } from "@/interfaces/organization";

type TreeNode = GroupNode & { children: TreeNode[] };
type Row = { node: GroupNode; depth: number };

/** Builds a sorted tree (by name at each level) from the flat group list. */
function buildTree(flat: GroupNode[]): TreeNode[] {
  const byId = new Map<number, TreeNode>();
  for (const g of flat) byId.set(g.id, { ...g, children: [] });
  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    if (node.parentId == null) roots.push(node);
    else byId.get(node.parentId)?.children.push(node);
  }
  const sortRec = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    for (const n of nodes) sortRec(n.children);
  };
  sortRec(roots);
  return roots;
}

/** Depth-first flatten — the table conveys hierarchy by indentation, as in the design. */
function flattenTree(nodes: TreeNode[], depth = 0): Row[] {
  return nodes.flatMap((n) => [{ node: n, depth }, ...flattenTree(n.children, depth + 1)]);
}

/**
 * A group's credit pool = its active, non-expired balance (from listGroupsForOrg). Single source
 * for both the row cell and the "Total pooled" sum in the header.
 */
function creditPoolOf(node: GroupNode): number {
  return Number(node.creditBalance ?? 0);
}

const badge = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  padding: "2px 8px",
  borderRadius: 9999,
  whiteSpace: "nowrap" as const,
  marginLeft: 8,
  background: "var(--color-good-night-4)",
  color: "var(--color-good-night)",
};

// Mirrors the design's `.history-table th` / `td` rules.
const th: CSSProperties = {
  textAlign: "left",
  padding: "12px 16px",
  background: "var(--color-wax)",
  fontSize: 11,
  fontWeight: 700,
  color: "var(--color-graphite)",
  textTransform: "uppercase",
  letterSpacing: "1px",
  borderBottom: "1px solid var(--color-smoke)",
  whiteSpace: "nowrap",
};

const td: CSSProperties = {
  padding: "14px 16px",
  fontSize: 14,
  color: "var(--color-rodeo-black)",
  borderBottom: "1px solid var(--color-smoke)",
  whiteSpace: "nowrap",
};

/** Inline rename field — a bordered text box, as in the design. */
const nameInputSx = {
  fontFamily: "var(--font-lato)",
  fontSize: 14,
  fontWeight: 600,
  color: "var(--color-rodeo-black)",
  border: "1px solid var(--color-smoke)",
  borderRadius: "6px",
  background: "var(--color-white)",
  padding: "8px 10px",
  minWidth: 180,
  "&:hover": { borderColor: "var(--color-dark-dust)" },
  "&:focus": {
    outline: "none",
    borderColor: "var(--color-primary-active)",
  },
} as const;

export default function GroupTree({
  org,
  deepLink,
  onDeepLinkConsumed,
}: {
  org: Organization;
  /**
   * One-shot deep link from the Plan tab's upgrade drawer ("Add payment method" → Account →
   * Groups → that group → Payment Methods), forwarded by OrgDetail. Read as a message, never as
   * the router state directly: this component unmounts whenever another org tab is showing, so
   * reading the (still-present) router state on every mount would replay an old link forever.
   */
  deepLink?: AccountDeepLink | null;
  /**
   * Tells OrgDetail the message has been applied, so it is not handed over a second time.
   * Must be referentially stable (OrgDetail wraps it in `useCallback`) — it is a dependency of
   * the effect below, which sets state, so a fresh identity each render would re-fire it.
   */
  onDeepLinkConsumed?: () => void;
}) {
  const { data: groups = [], isLoading } = useGroups(org.id);
  const deleteGroup = useDeleteGroup(org.id);
  const updateGroup = useUpdateGroup(org.id);
  const { can } = usePermissions();
  // Managers get edit only (no create/delete); Client Admins & GFG get all three.
  const canCreate = can("create", "GROUP");
  const canEdit = can("edit", "GROUP");
  const canDelete = can("delete", "GROUP");
  const [childParent, setChildParent] = useState<{ id: number; name: string } | null>(null);
  const { nonce, groupId: linkGroupId, groupTab } = deepLink ?? {};
  // An id that isn't in this org's list simply resolves to no group, leaving the hierarchy up.
  const [viewGroupId, setViewGroupId] = useState<number | null>(linkGroupId ?? null);
  // The group-tab half is kept locally so it survives OrgDetail clearing the message, and so a
  // group opened manually afterwards (View icon) opens on Details, not on the deep-linked tab.
  const [groupLink, setGroupLink] = useState<AccountDeepLink | null>(deepLink ?? null);
  const [error, setError] = useState<string | null>(null);

  /** Opens a group the user picked themselves — no deep link involved, so drop the message. */
  function openGroup(id: number) {
    setViewGroupId(id);
    setGroupLink(null);
  }

  // Applying the message is a sync, not just an initial value: this can also be already mounted
  // when one arrives. Keyed on `nonce`, so the same group/tab requested twice is still two
  // events; "← Groups" and a manual View in between are therefore not fought over, because
  // neither produces a new nonce.
  useEffect(() => {
    if (linkGroupId == null && !groupTab) return;
    if (linkGroupId != null) setViewGroupId(linkGroupId);
    setGroupLink({ nonce, groupId: linkGroupId, groupTab });
    onDeepLinkConsumed?.();
  }, [nonce, linkGroupId, groupTab, onDeepLinkConsumed]);

  function handleRemove(node: GroupNode) {
    if (!window.confirm(`Remove group “${node.name}”?`)) return;
    deleteGroup.mutate(node.id, {
      onError: (e) => setError(e instanceof Error ? e.message : "Failed to remove group."),
    });
  }

  /** Saves an inline rename on blur/Enter. Empty or unchanged input reverts, never saves. */
  function handleRename(node: GroupNode, raw: string, input: HTMLInputElement) {
    const name = raw.trim();
    if (!name || name === node.name) {
      input.value = node.name;
      return;
    }
    updateGroup.mutate(
      { id: node.id, patch: { name } },
      {
        onError: (e) => {
          input.value = node.name; // put the old name back if the save failed
          setError(e instanceof Error ? e.message : "Failed to rename group.");
        },
      },
    );
  }

  function handleLever(node: GroupNode, checked: boolean) {
    updateGroup.mutate(
      { id: node.id, patch: { creditPurchaseAllowed: checked } },
      { onError: (e) => setError(e instanceof Error ? e.message : "Failed to update group.") },
    );
  }

  const rows = flattenTree(buildTree(groups));
  const totalPooled = rows.reduce((sum, r) => sum + creditPoolOf(r.node), 0);

  // Resolved from the live list (not a stored snapshot) so it stays fresh after a save.
  const viewGroup = viewGroupId == null ? null : (groups.find((g) => g.id === viewGroupId) ?? null);

  // A selected group replaces the hierarchy with its own detail view.
  if (viewGroup) {
    return (
      <GroupView
        group={viewGroup}
        organizationId={org.id}
        onBack={() => {
          setViewGroupId(null);
          setGroupLink(null);
        }}
        deepLink={groupLink}
      />
    );
  }

  return (
    <Box>
      {/* Section header — mirrors the design's .history-head + .bc-card-title. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2.5,
          flexWrap: "wrap",
        }}
      >
        <Box
          component="h2"
          sx={{
            m: 0,
            fontFamily: "var(--font-crimson)",
            fontSize: 22,
            fontWeight: 400,
            lineHeight: 1.2,
            color: "var(--color-rodeo-black)",
          }}
        >
          Organize your teams into groups
        </Box>
        <Box sx={{ color: "var(--color-graphite)", fontSize: 14 }}>
          Total pooled: <strong>{totalPooled.toLocaleString()}</strong> credits
        </Box>
      </Box>

      <Box
        sx={{
          border: "1px solid var(--color-card-border)",
          borderRadius: "var(--radius-card)",
          bgcolor: "background.paper",
          overflowX: "auto",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
          <thead>
            <tr>
              <th style={th}>Group</th>
              <th style={th}>Members</th>
              <th style={{ ...th, textAlign: "right" }}>Credit pool</th>
              <th style={th}>May buy credits</th>
              <th style={{ ...th, textAlign: "right" }} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} style={{ ...td, textAlign: "center", padding: 40 }}>
                  <CircularProgress aria-label="Loading" size={22} />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  style={{
                    ...td,
                    textAlign: "center",
                    padding: 40,
                    color: "var(--color-graphite)",
                  }}
                >
                  No groups yet.
                </td>
              </tr>
            ) : (
              rows.map(({ node, depth }) => {
                const isRoot = node.parentId === null;
                return (
                  <Box
                    component="tr"
                    key={node.id}
                    sx={{ "&:hover": { bgcolor: "var(--color-wax)" } }}
                  >
                    {/* Hierarchy is shown by indentation + a branch glyph, as in the design. */}
                    <td style={{ ...td, paddingLeft: 16 + depth * 22 }}>
                      {depth > 0 && (
                        <span style={{ color: "var(--color-dark-dust)", marginRight: 6 }}>└</span>
                      )}
                      {canEdit ? (
                        <Box
                          component="input"
                          // Uncontrolled + keyed on the name so it re-syncs after a save.
                          key={node.name}
                          defaultValue={node.name}
                          placeholder="Group name"
                          aria-label={`Rename ${node.name}`}
                          disabled={updateGroup.isPending}
                          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === "Enter") e.currentTarget.blur();
                          }}
                          onBlur={(e: FocusEvent<HTMLInputElement>) =>
                            handleRename(node, e.target.value, e.target)
                          }
                          sx={nameInputSx}
                        />
                      ) : (
                        <span style={{ fontWeight: 600 }}>{node.name}</span>
                      )}
                      {isRoot && <span style={badge}>Root</span>}
                    </td>
                    <td style={td}>{node.userCount}</td>
                    <td style={{ ...td, textAlign: "right" }}>
                      {creditPoolOf(node).toLocaleString()}
                    </td>
                    <td style={td}>
                      <Switch
                        size="small"
                        // The root group must always be able to purchase credits.
                        checked={isRoot ? true : node.creditPurchaseAllowed}
                        disabled={isRoot || !canEdit || updateGroup.isPending}
                        title={
                          isRoot
                            ? "The root group must always be able to purchase credits"
                            : undefined
                        }
                        onChange={(e) => handleLever(node, e.target.checked)}
                      />
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <IconButton size="small" title="View" onClick={() => openGroup(node.id)}>
                        <VisibilityOutlined
                          sx={{ fontSize: 18, color: "var(--color-dark-dust)" }}
                        />
                      </IconButton>
                      {canCreate && (
                        <IconButton
                          size="small"
                          title="Add child group"
                          onClick={() => setChildParent({ id: node.id, name: node.name })}
                        >
                          <AddOutlined sx={{ fontSize: 18, color: "var(--color-dark-dust)" }} />
                        </IconButton>
                      )}
                      {canDelete && !isRoot ? (
                        <IconButton
                          size="small"
                          title="Remove"
                          disabled={deleteGroup.isPending}
                          onClick={() => handleRemove(node)}
                        >
                          <DeleteOutlineOutlined
                            sx={{ fontSize: 18, color: "var(--color-good-wine)" }}
                          />
                        </IconButton>
                      ) : null}
                    </td>
                  </Box>
                );
              })
            )}
          </tbody>
        </table>
      </Box>

      <GroupChildDialog
        parent={childParent}
        organizationId={org.id}
        onClose={() => setChildParent(null)}
      />
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError(null)} variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
